/**
 * Édition d'un aliment de la base personnelle (specs §6.4).
 *
 * Ce formulaire sert à **deux écrans**, et c'est délibéré :
 *   - l'onglet Bibliothèque, pour créer et corriger des fiches à tête reposée ;
 *   - la feuille d'ajout du journal, quand un code-barres est inconnu ou qu'un
 *     produit OFF doit être corrigé avant d'être ajouté (§6.4).
 * Une fiche d'aliment est un objet du référentiel : son éditeur vit donc avec
 * les objets, à la Bibliothèque, et l'écran d'ajout l'emprunte.
 *
 * ⚠️ **Enregistrer une correction n'a aucun effet sur le journal déjà saisi**
 * (§4.1, specs §7.1). L'écran le dit explicitement : c'est contre-intuitif —
 * on s'attend à ce que corriger une erreur corrige l'historique — et la
 * surprise coûterait cher dans les deux sens.
 *
 * ⚠️ Les macros saisies ici sont celles **pour 100 g / 100 ml**. Ce n'est pas
 * une entorse au §5.1 : ce que le serveur recalcule toujours lui-même, c'est le
 * total d'une entrée de journal, à partir de ces valeurs-là.
 */

import type { BaseUnit, FoodDTO } from '../../../../shared/types';
import { Button, GlyphClose } from '../../components/nourri';
import { Field, Hint, NumberField, Sheet } from '../../components/ui/primitives';
import { newClientId } from '../../lib/dates';
import { gapOf, macrosOf, type MacroFields } from '../../lib/formFields';
import { MACRO_ROWS } from '../../lib/macros';
import {
  isFoodDraftValid,
  type FoodDraft,
  type PortionDraft,
} from './foodDraft';

/** Segments d'unité de base — deux valeurs, pas de menu déroulant. */
function UnitToggle({
  value,
  onChange,
}: {
  value: BaseUnit;
  onChange: (unit: BaseUnit) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
      <span className="n-field__label">Macros exprimées pour</span>
      <div style={{ display: 'flex', gap: '0.4rem' }}>
        {(['g', 'ml'] as const).map((unit) => (
          <Button
            key={unit}
            aria-pressed={value === unit}
            variant={value === unit ? 'selected' : 'secondary'}
            shape="rounded"
            onClick={() => {
              onChange(unit);
            }}
          >
            100 {unit}
          </Button>
        ))}
      </div>
    </div>
  );
}

function PortionRows({
  portions,
  baseUnit,
  onChange,
}: {
  portions: PortionDraft[];
  baseUnit: BaseUnit;
  onChange: (next: PortionDraft[]) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <span className="n-field__label">Portions nommées</span>

      {portions.length === 0 && (
        <Hint>
          Aucune portion. Une portion évite de peser : « 1 tranche = 32 {baseUnit} » permet
          d’ajouter deux tranches sans calcul.
        </Hint>
      )}

      {portions.map((portion, index) => (
        <div key={portion.key} style={{ display: 'flex', gap: '0.4rem', alignItems: 'flex-end' }}>
          <div style={{ flex: 2, minWidth: 0 }}>
            <Field
              label="Libellé"
              value={portion.label}
              placeholder="1 tranche"
              onChange={(value) => {
                onChange(portions.map((item, i) => (i === index ? { ...item, label: value } : item)));
              }}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <NumberField
              label="Vaut"
              suffix={baseUnit}
              value={portion.quantity}
              onChange={(value) => {
                onChange(
                  portions.map((item, i) => (i === index ? { ...item, quantity: value } : item)),
                );
              }}
            />
          </div>
          <Button
            variant="secondary"
            shape="circle"
            aria-label={`Retirer ${portion.label === '' ? 'cette portion' : portion.label}`}
            onClick={() => {
              onChange(portions.filter((_, i) => i !== index));
            }}
          >
            <GlyphClose />
          </Button>
        </div>
      ))}

      <Button
        variant="ghost"
        shape="rounded"
        onClick={() => {
          onChange([...portions, { key: newClientId(), label: '', quantity: '' }]);
        }}
        style={{ alignSelf: 'flex-start' }}
      >
        Ajouter une portion
      </Button>
    </div>
  );
}

/**
 * Le formulaire seul, sans feuille modale : c'est lui que la feuille d'ajout
 * réutilise, à l'intérieur de **sa** feuille — deux feuilles empilées seraient
 * une impasse gestuelle sur iPhone.
 */
export function FoodForm({
  draft,
  onDraftChange,
  onSubmit,
  submitLabel,
  saving,
  footnote,
}: {
  draft: FoodDraft;
  onDraftChange: (next: FoodDraft) => void;
  onSubmit: () => void;
  submitLabel: string;
  saving?: boolean;
  footnote?: string;
}) {
  const set = <K extends keyof FoodDraft>(key: K) => (value: FoodDraft[K]) => {
    onDraftChange({ ...draft, [key]: value });
  };
  const setMacro = (key: keyof MacroFields) => (value: string) => {
    onDraftChange({ ...draft, per100: { ...draft.per100, [key]: value } });
  };

  const per100 = macrosOf(draft.per100);
  const gap = gapOf(per100);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (isFoodDraftValid(draft)) onSubmit();
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
    >
      <Field label="Nom" value={draft.name} onChange={set('name')} placeholder="Blanc de poulet" />
      <Field label="Marque" value={draft.brand} onChange={set('brand')} placeholder="facultatif" />
      <Field
        label="Code-barres"
        value={draft.barcode}
        onChange={set('barcode')}
        placeholder="facultatif"
      />

      <UnitToggle value={draft.baseUnit} onChange={set('baseUnit')} />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: '0.5rem',
        }}
      >
        {MACRO_ROWS.map((row) => (
          <NumberField
            key={row.key}
            label={row.short}
            suffix={row.key === 'kcal' ? undefined : 'g'}
            value={draft.per100[row.key]}
            onChange={setMacro(row.key)}
          />
        ))}
      </div>

      {/* Écart 4/4/9 — indicatif, ne propose rien (specs §6.2, archi §5.3). */}
      {gap.delta !== 0 && per100.kcal > 0 && (
        <p className="n-hint">
          {gap.delta > 0 ? '+' : '−'}
          {Math.abs(gap.delta).toLocaleString('fr-FR')} kcal par rapport au 4/4/9 de P/G/L (
          {gap.theoretical.toLocaleString('fr-FR')} kcal). Indicatif.
        </p>
      )}

      <PortionRows
        portions={draft.portions}
        baseUnit={draft.baseUnit}
        onChange={set('portions')}
      />

      <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', minHeight: '44px' }}>
        <input
          type="checkbox"
          checked={draft.isFavorite}
          onChange={(event) => {
            onDraftChange({ ...draft, isFavorite: event.target.checked });
          }}
          className="ui-check"
        />
        <span style={{ fontSize: '1rem' }}>Favori — proposé dans l’accès rapide</span>
      </label>

      <Button
        type="submit"
        variant="primary"
        shape="rounded"
        block
        disabled={!isFoodDraftValid(draft) || saving === true}
      >
        {saving === true ? 'Enregistrement…' : submitLabel}
      </Button>

      <Hint>
        {footnote ??
          'Corriger une fiche ne modifie jamais les entrées déjà enregistrées dans le journal : leurs macros sont figées à la saisie.'}
      </Hint>
    </form>
  );
}

/** Le même formulaire, dans sa propre feuille — usage Bibliothèque. */
export function FoodEditorSheet({
  open,
  title,
  draft,
  onDraftChange,
  onSubmit,
  onClose,
  saving,
  existing,
}: {
  open: boolean;
  title: string;
  draft: FoodDraft;
  onDraftChange: (next: FoodDraft) => void;
  onSubmit: () => void;
  onClose: () => void;
  saving?: boolean;
  /** Fiche en cours de modification, pour rappeler sa provenance. */
  existing?: FoodDTO;
}) {
  return (
    <Sheet open={open} title={title} onClose={onClose}>
      {existing?.source === 'openfoodfacts' && (
        <Hint>
          Fiche importée d’Open Food Facts (code {existing.offCode ?? '—'}). Les valeurs sont
          librement corrigeables : c’est votre fiche désormais, et c’est elle qui prime.
        </Hint>
      )}
      <FoodForm
        draft={draft}
        onDraftChange={onDraftChange}
        onSubmit={onSubmit}
        saving={saving}
        submitLabel={existing ? 'Enregistrer' : 'Créer l’aliment'}
      />
    </Sheet>
  );
}
